import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { editor } from 'monaco-editor';
import React, { Fragment, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { FrameworkClass } from '../../../framework/framework.d';
import { FrameworkClassPartType, FrameworkClassType } from '../../../framework/framework-types';
import DeviceUiBuilder, { ICardComponent, removeHardwareCardAssignments } from '../Devices/DeviceCardBuilder';

interface ConfigureHardwareTabProps {
  dynamicCards: ICardComponent[];
  isLoading: boolean;
  /** Live Monaco instance so device controls can read constructor args (e.g. motor port) after mount. */
  editor?: editor.IStandaloneCodeEditor | null;
  /** Incremented when the parsed hardware card set changes so port occupancy re-syncs in Redux. */
  driveUnitPortRegistryRev?: number;
}

const ACTIVE_CARD_GROUP = {
  mechanisms: 'mechanisms',
  motors: 'motors',
  sensors: 'sensors',
  other: 'other',
} as const;

type ActiveCardGroup = (typeof ACTIVE_CARD_GROUP)[keyof typeof ACTIVE_CARD_GROUP];

const FRAMEWORK_CLASS_NAME = {
  driveBase: 'DriveBase',
} as const;

const ConfigureHardwareTab: React.FC<ConfigureHardwareTabProps> = ({ dynamicCards, isLoading, editor, driveUnitPortRegistryRev }) => {
  const cardsWithEditor = useMemo(
    () =>
      dynamicCards.map((card) => ({
        ...card,
        props: {
          ...card.props,
          editor: editor ?? card.props.editor,
          driveUnitPortRegistryRev,
        },
      })),
    [dynamicCards, driveUnitPortRegistryRev, editor]
  );

  const groupedCards = useMemo(() => {
    const grouped: Record<ActiveCardGroup, ICardComponent[]> = {
      [ACTIVE_CARD_GROUP.mechanisms]: [],
      [ACTIVE_CARD_GROUP.motors]: [],
      [ACTIVE_CARD_GROUP.sensors]: [],
      [ACTIVE_CARD_GROUP.other]: [],
    };

    const classifyActiveCard = (card: ICardComponent): ActiveCardGroup => {
      const mechanismClass = card?.props?.mechanismClass as FrameworkClass | undefined;
      const className = String(mechanismClass?.name || '');
      const classType = mechanismClass?.type;
      const partType = mechanismClass?.partType;

      if (classType === FrameworkClassType.Mechanism) return ACTIVE_CARD_GROUP.mechanisms;
      if (className === FRAMEWORK_CLASS_NAME.driveBase) return ACTIVE_CARD_GROUP.mechanisms;
      if (partType === FrameworkClassPartType.Motors) return ACTIVE_CARD_GROUP.motors;
      if (partType === FrameworkClassPartType.Sensors) return ACTIVE_CARD_GROUP.sensors;
      if (partType === FrameworkClassPartType.Other) return ACTIVE_CARD_GROUP.other;
      if (/motor/i.test(className)) return ACTIVE_CARD_GROUP.motors;
      if (/sensor/i.test(className)) return ACTIVE_CARD_GROUP.sensors;
      return ACTIVE_CARD_GROUP.other;
    };

    for (const card of cardsWithEditor) {
      grouped[classifyActiveCard(card)].push(card);
    }

    return grouped;
  }, [cardsWithEditor]);

  const activeMechanismsContent = (
    <Box sx={{ p: 1 }}>
      <Box textAlign="center" width="100%" p={2}>
        <Typography textAlign="center">
          <Trans i18nKey="editorDrawer.tabs.activeProjectElectronics">Active Project Electronics</Trans>
        </Typography>
      </Box>
      {cardsWithEditor.length !== 0 ? (
        <Stack direction="column" spacing={1}>
          {(() => {
            const canDeleteCard = (card: ICardComponent) => card.props.mechanismName === card.props.parentName;
            const renderGroup = (opts: { titleKey: string; defaultTitle: string; cards: ICardComponent[] }) => {
              if (opts.cards.length === 0) return null;
              return (
                <Stack direction="column" spacing={1}>
                  <Box sx={{ pt: 1.5, pb: 0.5, px: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      <Trans i18nKey={opts.titleKey}>{opts.defaultTitle}</Trans>
                    </Typography>
                  </Box>
                  {opts.cards.map((card) => (
                    <Fragment key={card.props.id}>
                      {DeviceUiBuilder({
                        ...card,
                        props: {
                          ...card.props,
                          initialOpen: false,
                          onDelete: canDeleteCard(card) ? () => removeHardwareCardAssignments(card.props) : undefined,
                        },
                      })}
                    </Fragment>
                  ))}
                </Stack>
              );
            };

            return (
              <Stack direction="column" spacing={2}>
                {renderGroup({
                  titleKey: 'configurationDrawer.activeSections.mechanisms',
                  defaultTitle: 'Active Mechanisms',
                  cards: groupedCards[ACTIVE_CARD_GROUP.mechanisms],
                })}
                {renderGroup({
                  titleKey: 'configurationDrawer.activeSections.motors',
                  defaultTitle: 'Active Motors',
                  cards: groupedCards[ACTIVE_CARD_GROUP.motors],
                })}
                {renderGroup({
                  titleKey: 'configurationDrawer.activeSections.sensors',
                  defaultTitle: 'Active Sensors',
                  cards: groupedCards[ACTIVE_CARD_GROUP.sensors],
                })}
                {renderGroup({
                  titleKey: 'configurationDrawer.activeSections.otherElectronics',
                  defaultTitle: 'Other Active Electronics',
                  cards: groupedCards[ACTIVE_CARD_GROUP.other],
                })}
              </Stack>
            );
          })()}
        </Stack>
      ) : isLoading ? (
        <Stack direction="column" spacing={1}>
          <Box display="flex" height="calc(100vh - 300px)" justifyContent={'center'} alignItems={'center'}>
            <CircularProgress />
          </Box>
        </Stack>
      ) : (
        <Stack direction="column" spacing={1}>
          <Box display="flex" height="calc(100vh - 300px)" justifyContent={'center'} alignItems={'center'}>
            <Typography
              variant="sectionTitle"
              sx={{
                textAlign: 'center',
                flexGrow: 1,
              }}
            >
              <Trans i18nKey="configurationDrawer.noActiveElectronics">No active electronics</Trans>
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );

  return <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>{activeMechanismsContent}</Box>;
};

export default ConfigureHardwareTab;
